import React, { useRef, useState } from "react";
import { Tab, TabList, OverflowItem, makeStyles, mergeClasses } from "@fluentui/react-components";
import { themeTokens } from "../../utils/Theming";

const useStyles = makeStyles({
  dragging: {
    cursor: "grabbing",
  },
  tabListContainer: {
    padding: "0 20px",
    overflowX: "auto",
    display: "flex",
    whiteSpace: "nowrap",
    position: "relative",
    backgroundColor: themeTokens.colorNeutralBackground2,
    cursor: "grab",
    scrollbarWidth: "none",
    "-ms-overflow-style": "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  tabItem: {
    flexGrow: 0,
    flexShrink: 0,
    padding: "8px 10px",
    display: "inline-flex",
  },
  tabContent: {
    padding: "20px",
    overflowY: "auto",
    backgroundColor: themeTokens.colorNeutralBackground1,
  },
  tabContantWithPageHeader: {
    height: "calc(100vh - 258px)",
  },
  tabContantWithoutPageHeader: {
    height: "calc(100vh - 204px)",
  },
});

interface TabContentProps {
  selectedTab: string;
  skipPageHeader?: boolean;
  onTabChange: (tabId: string) => void;
  children: React.ReactNode;
}

interface TabElementProps {
  value: string;
  name: string;
  children: React.ReactNode;
}

const TabContent: React.FC<TabContentProps> = ({ selectedTab, skipPageHeader, onTabChange, children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState<number | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const tabListRef = useRef<HTMLDivElement | null>(null);
  const styles = useStyles();

  const onMouseDown = (e: React.MouseEvent) => {
    if (tabListRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - tabListRef.current.offsetLeft);
      setScrollLeft(tabListRef.current.scrollLeft);
    }
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || startX === null || !tabListRef.current) return;

    const x = e.pageX - tabListRef.current.offsetLeft;
    const walk = x - startX;
    tabListRef.current.scrollLeft = scrollLeft - walk;
  };

  const onMouseUpOrLeave = () => {
    setIsDragging(false);
    setStartX(null);
  };

  const tabs = React.Children.map(children, (child) => {
    if (React.isValidElement<TabElementProps>(child) && child.props.value) {
      return (
        <OverflowItem
          key={child.props.value}
          id={child.props.value}
          priority={child.props.value === selectedTab ? 2 : 1}
        >
          <Tab className={styles.tabItem} value={child.props.value}>
            {child.props.name}
          </Tab>
        </OverflowItem>
      );
    }
    return null;
  });

  const content = React.Children.toArray(children).find(
    (child): child is React.ReactElement<TabElementProps> =>
      React.isValidElement(child) && child.props.value === selectedTab
  );

  return (
    <>
      <div
        className={mergeClasses(styles.tabListContainer, isDragging && styles.dragging)}
        ref={tabListRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUpOrLeave}
        onMouseLeave={onMouseUpOrLeave}
      >
        <TabList
          selectedValue={selectedTab}
          onTabSelect={(_, d) => onTabChange(d.value as string)}
          reserveSelectedTabSpace={false}
          selectTabOnFocus={true}
          size="small"
        >
          {tabs}
        </TabList>
      </div>

      <div
        className={mergeClasses(
          styles.tabContent,
          skipPageHeader ? styles.tabContantWithoutPageHeader : styles.tabContantWithPageHeader
        )}
      >
        {content && content.props.children}
      </div>
    </>
  );
};

export default TabContent;
